import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { toast } from "sonner";
import { Plus, BellRing } from "lucide-react";
import { api, formatApiError } from "../../booking/api";
import { athensToday, athensNowHHMM } from "../../booking/utils";
import { UnitCard } from "./components/UnitCard";
import { BookingChip } from "./components/BookingChip";
import { WalkInDialog } from "./components/WalkInDialog";
import { DayToolbar } from "./components/DayToolbar";

export default function BeachCalendarPage() {
  const [date, setDate] = useState(athensToday());
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [meta, setMeta] = useState({ groups: [], statuses: [], services: [] });
  const [refreshing, setRefreshing] = useState(false);
  const [walkIn, setWalkIn] = useState(null);
  const latestDate = useRef(date);
  latestDate.current = date;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }));

  useEffect(() => {
    Promise.all(["resource-groups", "statuses", "services"].map((e) => api.get(`/api/admin/${e}`)))
      .then(([g, s, sv]) => setMeta({ groups: g.data, statuses: s.data, services: sv.data.filter((x) => x.active) }));
  }, []);

  const load = useCallback(async () => {
    setRefreshing(true);
    const reqDate = date;
    try {
      const [b, w] = await Promise.all([
        api.get("/api/admin/bookings", { params: { date_from: date, date_to: date, with_history: true } }),
        api.get("/api/admin/waitlist", { params: { date_from: date } }),
      ]);
      if (reqDate !== latestDate.current) return;
      setBookings(b.data.filter((x) => x.status !== "Cancelled"));
      setWaitlist(w.data.filter((x) => x.date === date && x.status === "waiting"));
    } finally {
      setRefreshing(false);
    }
  }, [date]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const isToday = date === athensToday();
  const colorOf = (name) => meta.statuses.find((s) => s.name === name)?.color || "#0d0c0c";
  const isLate = (b) => {
    if (!isToday || !b.resource_group_id || b.status !== "BOOKED") return false;
    const now = athensNowHHMM();
    return now >= "13:00" && now >= b.time;
  };

  const update = async (b, patch, msg) => {
    setBookings((rs) => rs.map((x) => (x.id === b.id ? { ...x, ...patch } : x)));
    try {
      const r = await api.put(`/api/admin/bookings/${b.id}`, patch);
      setBookings((rs) => rs.map((x) => (x.id === b.id ? { ...r.data, history: x.history } : x)));
      toast.success(msg || `${b.code} → ${patch.status}`);
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const onDragEnd = ({ active, over }) => {
    if (!over) return;
    const b = active.data.current.booking;
    const { group, unit } = over.data.current;
    if (b.resource_unit === unit.name) return;
    if (b.resource_group_id !== group.id) return toast.error(`${b.service_name} can only move within ${meta.groups.find((g) => g.id === b.resource_group_id)?.name}`);
    update(b, { resource_unit: unit.name }, `${b.code} moved to ${unit.name}`);
  };

  const others = bookings.filter((b) => !b.resource_group_id);
  const stats = useMemo(() => {
    const units = meta.groups.flatMap((g) => g.units.filter((u) => u.active).map((u) => u.name));
    const used = new Set(bookings.filter((b) => b.status !== "DEPARTED").map((b) => b.resource_unit).filter(Boolean));
    return { bookings: bookings.length, pax: bookings.reduce((s, b) => s + b.guests, 0),
      arrived: bookings.filter((b) => b.status === "ARRIVED").length, free: units.filter((u) => !used.has(u)).length };
  }, [bookings, meta.groups]);

  return (
    <div data-testid="admin-calendar-page">
      <h1 className="text-2xl font-semibold mb-4">Beach Calendar</h1>
      <DayToolbar date={date} setDate={setDate} stats={stats} refreshing={refreshing} onRefresh={load} />
      <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="grid lg:grid-cols-[1fr_260px] gap-6 mt-6">
          <div className="space-y-6">
            {meta.groups.map((g) => (
              <section key={g.id} data-testid={`cal-group-${g.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-2">{g.name}</h2>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-2">
                  {g.units.filter((u) => u.active).map((u) => (
                    <UnitCard key={u.name} group={g} unit={u} colorOf={colorOf} isLate={isLate}
                      bookings={bookings.filter((b) => b.resource_group_id === g.id && b.resource_unit === u.name)}
                      onStatus={(b, status) => update(b, { status })} onWalkIn={(group, unit) => setWalkIn({ group, unit })}
                      onNoteSaved={load} />
                  ))}
                </div>
              </section>
            ))}
          </div>
          <aside className="space-y-6">
            <section data-testid="cal-other-bookings">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">Massage & Padel</h2>
                <button onClick={() => setWalkIn({ group: null, unit: null })} data-testid="cal-walkin-other"
                  className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900"><Plus size={13} /> Walk-in</button>
              </div>
              <div className="space-y-2">
                {others.map((b) => (
                  <div key={b.id}>
                    <p className="text-[11px] text-stone-500 mb-0.5 truncate">{b.service_name}</p>
                    <BookingChip booking={b} color={colorOf(b.status)} late={false} draggable={false} onStatus={(x, status) => update(x, { status })} onNoteSaved={load} />
                  </div>
                ))}
                {!others.length && <p className="text-xs text-stone-400">No bookings.</p>}
              </div>
            </section>
            <section data-testid="cal-waitlist">
              <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-2 flex items-center gap-1.5">
                <BellRing size={13} /> Waiting list ({waitlist.length})
              </h2>
              {waitlist.map((w) => (
                <div key={w.id} className="bg-white border rounded-md p-2.5 text-xs mb-2" data-testid={`cal-waitlist-${w.id}`}>
                  <div className="font-semibold">{w.name} · {w.guests} pax</div>
                  <div className="text-stone-500">{w.service_name}</div>
                  <div className="text-stone-500">{w.phone} · {w.email}</div>
                </div>
              ))}
              {!waitlist.length && <p className="text-xs text-stone-400">Nobody waiting.</p>}
            </section>
          </aside>
        </div>
      </DndContext>
      <WalkInDialog target={walkIn} date={date} isToday={isToday} nowHHMM={athensNowHHMM} services={meta.services}
        onClose={() => setWalkIn(null)} onCreated={load} />
    </div>
  );
}
